<template>
    <ListWrapper
        :loading="loading"
        :error="error"
        :is-empty="isEmpty"
        :meta="meta"
        :page="page"
        :per-page="perPage"
        :show-pagination="meta.last_page > 1"
        @fetch="onFetch"
    >
        <template #filters>
            <CardFront custom-classes="tw:mb-3 tw:py-4 tw:px-4">
                <div class="tw:grid tw:grid-cols-1 tw:gap-3 tw:md:grid-cols-[1fr_220px] tw:md:gap-3">
                    <InputText
                        :value="search"
                        name="search"
                        :label="$t('perfil.buscar_deportista')"
                        @input="onSearchInput"
                    />
                    <InputMulti
                        v-if="listas.length > 0"
                        :value="selectedListaId"
                        :options="listas"
                        label-key="nombre"
                        :label="$t('perfil.filtrar_lista_abonados')"
                        :multiple="false"
                        custom-class="tw:w-full"
                        @input="onListaInput"
                    />
                </div>
            </CardFront>
        </template>

        <template #error="{ error: err }">
            <CardFront custom-classes="tw:py-16 tw:text-center tw:items-center">
                <i class="material-icons tw:text-5xl tw:text-red-300">error_outline</i>
                <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">{{ $t('perfil.error_titulo') }}</p>
                <p class="tw:text-sm tw:text-slate-400 tw:mt-1">{{ err }}</p>
            </CardFront>
        </template>

        <template #empty>
            <CardFront custom-classes="tw:py-16 tw:text-center tw:items-center">
                <i class="material-icons tw:text-5xl tw:text-slate-300">group</i>
                <p class="tw:mt-4 tw:font-semibold tw:text-slate-700">{{ $t('perfil.no_plazos_cantera') }}</p>
            </CardFront>
        </template>

        <div
            v-if="!loading && !isEmpty"
            :class="['tw:flex tw:flex-col tw:gap-2', selectedCount > 0 ? 'tw:pb-24' : '']"
        >
            <Card
                v-for="deportista in deportistas"
                :key="deportista.id"
                :deportista="deportista"
                :detalle="detalleCache.get(deportista.id) || null"
                :detalle-error="detalleErrorCache.get(deportista.id) || null"
                :loading-detalle="detalleLoadingIds.has(deportista.id)"
                :is-selected="isSelected"
                @expand="$emit('expand', $event)"
                @plazo-toggled="onPlazoToggled"
            />
        </div>

        <SelectionBar
            :count="selectedCount"
            :total="totalSeleccionado"
            :cta-text="$t('perfil.pagar_seleccionados')"
            @open="openPaymentModal"
        />

        <PaymentModal
            :show="isPaymentModalOpen"
            :plazos="selectedPlazos"
            :total="totalSeleccionado"
            :loading="pagoLoading"
            :error="pagoError"
            :supports3DS="true"
            tipo-venta="plazosBase"
            @close="closePaymentModal"
            @pago-completado="handlePagoCompletado"
        />
    </ListWrapper>
</template>

<script setup>
    import { toRef } from 'vue';
    import Card from './Card.vue';
    import PaymentModal from '../plazos/PaymentModal.vue';
    import SelectionBar from '../plazos/SelectionBar.vue';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import InputMulti from '@/modules/front/ui/InputMulti.vue';
    import ListWrapper from '@/modules/front/ui/ListWrapper.vue';
    import { usePlazosSelection } from '@/composables/front/perfil/usePlazosSelection';
    import { usePlazosPaymentModal } from '@/composables/front/perfil/usePlazosPaymentModal';

    const props = defineProps({
        deportistas: {
            type: Array,
            required: true,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            default: null,
        },
        isEmpty: {
            type: Boolean,
            default: false,
        },
        meta: {
            type: Object,
            required: true,
        },
        page: {
            type: Number,
            required: true,
        },
        perPage: {
            type: Number,
            required: true,
        },
        search: {
            type: String,
            default: '',
        },
        detalleCache: {
            type: Map,
            required: true,
        },
        detalleLoadingIds: {
            type: Set,
            required: true,
        },
        detalleErrorCache: {
            type: Map,
            required: true,
        },
        isSelected: {
            type: Function,
            required: true,
        },
        selectedCount: {
            type: Number,
            default: 0,
        },
        totalSeleccionado: {
            type: Number,
            default: 0,
        },
        pagoLoading: {
            type: Boolean,
            default: false,
        },
        pagoError: {
            type: String,
            default: null,
        },
        onRefresh: {
            type: Function,
            default: null,
        },
        listas: {
            type: Array,
            default: () => [],
        },
        listasLoading: {
            type: Boolean,
            default: false,
        },
        selectedListaId: {
            type: [Number, String],
            default: null,
        },
    });

    const emit = defineEmits(['expand', 'fetch', 'plazo-toggled', 'pagar', 'pago-completado', 'update:search', 'update:lista-id']);

    const onFetch = (newPage, newPerPage) => {
        emit('fetch', newPage, newPerPage ?? props.perPage);
    };

    const onSearchInput = val => {
        emit('update:search', val);
    };

    const onListaInput = val => {
        emit('update:lista-id', val);
    };

    const {
        isOpen: isPaymentModalOpen,
        selectedPlazos,
        open: openPaymentModal,
        close: closePaymentModal,
    } = usePlazosPaymentModal({
        items: toRef(props, 'deportistas'),
        detalleCache: toRef(props, 'detalleCache'),
        isSelected: props.isSelected,
        loading: toRef(props, 'pagoLoading'),
        error: toRef(props, 'pagoError'),
        onConfirm: () => {},
    });

    const onPlazoToggled = payload => {
        emit('plazo-toggled', payload);
    };

    const handlePagoCompletado = () => {
        closePaymentModal();
        emit('pago-completado');
    };

    const onModalClose = () => {
        closePaymentModal();
        if (props.onRefresh) {
            props.onRefresh();
        }
    };
</script>